"use client";

import { useGetDietAddons } from "@repo/api/catalog/api/diet-addon-group";
import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import { IconPlus } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { AddonThumbnails } from "@/features/addons/components/addon-thumbnails";
import {
  AddonsConfigProvider,
  useAddonsConfigStore,
} from "@/features/addons/components/addons-config-provider";
import { DietAddonModal } from "@/features/addons/components/diet-addon-modal";
import { DietAddonsSummaryDialog } from "@/features/addons/components/diet-addons-summary-dialog";
import { findAddonCategoryName } from "@/features/addons/lib/addon-gtm-item";
import { summarizeSelection } from "@/features/addons/lib/aggregate-selected-addons";
import { groupSelectedAddonsBySeller } from "@/features/addons/lib/group-addons-by-seller";
import type { SubItemsPricing } from "@/features/addons/types/sub-Items-pricing.type";
import { useCartItemAddons } from "@/features/cart/hooks/use-cart-item-addons";
import type { CartItem } from "@/features/cart/types";
import { getLocalePrice } from "@/lib/utilities";
import { SummaryAddonsGroups } from "../configure-diet/summary/summary-addons-groups";
import { SummaryCollapsibleRow } from "../configure-diet/summary/summary-collapsible-row";
import { usePendingRemovals } from "../pending-removals-provider";
import { RemovedItemNotice } from "../remove-from-cart/removed-item-notice";

type CartDietAddonsContentProps = {
  categories: DietAddonGroupDietAddonsRead[];
  dietDates: Date[];
  itemId: string;
  // The diet's catering — its addons lead the per-seller groups (MPL-3833).
  dietMerchantId: string;
  // Addon quote of this position as the cart returned it.
  subItemsPricing: SubItemsPricing | null;
  cateringName?: string;
};

const CartDietAddonsContent = ({
  categories,
  dietDates,
  itemId,
  dietMerchantId,
  subItemsPricing,
  cateringName,
}: CartDietAddonsContentProps) => {
  const t = useTranslations();
  const entries = useAddonsConfigStore((store) => store.addons);
  const { cancel, getRemovalStatus, queue } = usePendingRemovals();
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [editedAddon, setEditedAddon] = useState<AddonDietAddonsRead | null>(
    null,
  );

  const summary = useMemo(
    () => summarizeSelection(categories, entries, subItemsPricing),
    [categories, entries, subItemsPricing],
  );
  // One diet can carry addons of its catering and Foodango products of other
  // merchants; the expanded list tells them apart per seller (MPL-3833). The
  // header amount stays the sum of everything.
  const sellerGroups = useMemo(
    () =>
      groupSelectedAddonsBySeller(summary.items, {
        dietMerchantId,
        cateringName,
      }),
    [summary.items, dietMerchantId, cateringName],
  );

  return (
    <>
      {summary.items.length === 0 ? (
        <div className="flex w-full items-center justify-between gap-3 rounded-b-xl border-t bg-brand-white p-4 md:p-6">
          <p className="font-medium text-base text-brand-green1-300 leading-6 md:text-xl md:leading-7">
            {t("cart.addons.empty")}
          </p>
          <button
            className="flex shrink-0 items-center gap-2 font-medium text-base text-brand-green1-300 underline underline-offset-4"
            onClick={() => setSummaryOpen(true)}
            type="button"
          >
            <IconPlus className="size-6" stroke={2} />
            {t("cart.addons.add")}
          </button>
        </div>
      ) : (
        // Same collapsible summary as the configuration aside, just full-width
        // under the diet card. Collapsed by default (design).
        <Accordion className="w-full" type="multiple">
          {/* The cart list scales the header up on desktop (24px padding, 20px
              label); mobile keeps the shared 16px defaults. */}
          <SummaryCollapsibleRow
            amount={getLocalePrice({ price: summary.productTotal })}
            amountClassName="text-brand-green1-300 md:text-xl"
            contentClassName="p-0"
            label={t("cart.configuration.addons.summaryLabel")}
            labelClassName="md:text-xl"
            leading={<AddonThumbnails items={summary.items} />}
            triggerClassName="md:px-6"
            value="addons"
          >
            <SummaryAddonsGroups
              cateringName={cateringName}
              groups={sellerGroups}
              headerClassName="px-4 py-3 md:px-6 md:py-4"
              listClassName="p-4 md:p-6"
              onEdit={setEditedAddon}
              onRemove={(addonId) =>
                queue({
                  kind: "addon",
                  itemId,
                  addonId,
                  categories,
                  cateringName,
                })
              }
              renderRemoved={(addonId) => {
                const removalStatus = getRemovalStatus(itemId, addonId);
                if (removalStatus === "idle") {
                  return null;
                }

                return (
                  <RemovedItemNotice
                    className="py-3"
                    label={t("cart.undo.addon")}
                    onUndo={
                      removalStatus === "undo"
                        ? () => cancel(itemId, addonId)
                        : undefined
                    }
                  />
                );
              }}
            >
              <Button
                className="mt-3 h-14 w-full text-base"
                onClick={() => setSummaryOpen(true)}
                type="button"
                variant="outline"
              >
                {t("cart.addons.addAnother")}
              </Button>
            </SummaryAddonsGroups>
          </SummaryCollapsibleRow>
        </Accordion>
      )}

      {editedAddon && (
        <DietAddonModal
          addon={editedAddon}
          cateringName={cateringName}
          categoryName={findAddonCategoryName(categories, editedAddon.id)}
          dietDates={dietDates}
          onOpenChange={(open) => {
            if (!open) {
              setEditedAddon(null);
            }
          }}
          open
          subItemsPricing={subItemsPricing}
        />
      )}

      <DietAddonsSummaryDialog
        cateringName={cateringName}
        categories={categories}
        dietDates={dietDates}
        onOpenChange={setSummaryOpen}
        open={summaryOpen}
        subItemsPricing={subItemsPricing}
      />
    </>
  );
};

// Expandable addons list under a diet on the cart list (MPL-3757). Reads the
// selection persisted in the item's sub-items and keeps it in sync with the
// cart through PUT /cart/{id}. Hidden entirely when the diet offers no addons.
export const CartDietAddons = ({
  dietId,
  item,
  cateringName,
}: {
  dietId: string;
  item: CartItem;
  // Reported as item_brand for addon events — an addon carries no brand of its
  // own, so the parent diet's catering is used.
  cateringName?: string;
}) => {
  const { data } = useGetDietAddons(dietId, undefined, {
    query: {
      staleTime: Number.POSITIVE_INFINITY,
      select: (response) => response.data,
    },
  });
  // One store and one quote for the whole selection of the position: the
  // catering's addons and the Foodango products riding with it (MPL-3829).
  const { store, pricing } = useCartItemAddons(item, {
    cateringName,
    categories: data ?? [],
  });

  const dietDates = useMemo(
    () => item.configurationItem.dates.map((date) => new Date(date)),
    [item.configurationItem.dates],
  );

  const hasAddons = data?.some((category) => category.addons.length > 0);
  if (!(data && hasAddons)) {
    return null;
  }

  return (
    <AddonsConfigProvider store={store}>
      <CartDietAddonsContent
        cateringName={cateringName}
        categories={data}
        dietDates={dietDates}
        dietMerchantId={item.configurationItem.merchantId}
        itemId={item.configurationItem.id}
        subItemsPricing={pricing}
      />
    </AddonsConfigProvider>
  );
};
